/**
 * Unified Settings Component
 * Consolidated settings interface with 8 organized tabs
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';
import {
  Building2,
  Palette,
  DollarSign,
  Percent,
  Receipt,
  FileText,
  CheckCircle,
  Star,
  Save,
  Loader2,
  Globe,
  Mail,
  Phone,
  MapPin,
  Upload,
  Wallet,
  Plus,
  Trash2,
  AlertCircle,
  Copy,
  Shield,
  Package,
  Calendar,
  RefreshCw,
  CreditCard,
  Volume2
} from 'lucide-react';

// Import existing components
import { WalletAddresses } from '@/components/WalletAddresses';
import { HotelVerificationPanel } from '@/components/verification/HotelVerificationPanel';
import { ReliabilitySettingsPanel } from '@/components/reliability/ReliabilitySettingsPanel';
import { VoiceConfigPanel } from '@/components/VoiceConfigPanel';

const PREVIEW_URL = window.location.origin;

export function UnifiedSettingsTab({ hotelId, hotel, user, onHotelUpdate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Hotel Info & Branding State
  const [hotelSettings, setHotelSettings] = useState({
    hotel_name: '',
    hotel_address: '',
    hotel_email: '',
    hotel_phone: '',
    hotel_website: '',
    hotel_logo_url: '',
    primary_color: '#10b981',
    secondary_color: '#3b82f6',
    currency: 'USD',
    currency_symbol: '$',
    registration_terms: '',
    folio_terms: ''
  });

  // Taxes State
  const [taxes, setTaxes] = useState([]);
  
  // Fees State
  const [fees, setFees] = useState([]);

  // Complaint Allowances State
  const [complaintAllowances, setComplaintAllowances] = useState([]);

  // Group Discounts State
  const [groupDiscounts, setGroupDiscounts] = useState([]);
  
  // Package Deals State
  const [packageDeals, setPackageDeals] = useState([]);
  
  // Cancellation Policy State
  const [cancellationPolicy, setCancellationPolicy] = useState([]);
  const [policyIsCustom, setPolicyIsCustom] = useState(false);

  // Stripe Connect State
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    account_id: null,
    charges_enabled: false,
    payouts_enabled: false,
    details_submitted: false,
    requirements: null
  });
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    loadSettings();
    loadStripeStatus();
  }, [hotelId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Load hotel settings
      const settingsResponse = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/settings`);
      const settingsData = await settingsResponse.json();
      
      if (settingsData.success && settingsData.settings) {
        setHotelSettings(prev => ({
          ...prev,
          ...settingsData.settings
        }));
      }

      // Load financial settings (taxes and fees)
      try {
        const financialResponse = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/financial-settings`);
        if (financialResponse.ok) {
          const financialData = await financialResponse.json();
          if (financialData.success) {
            setTaxes(financialData.taxes || []);
            setFees(financialData.fees || []);
          }
        }
      } catch (err) {
        console.warn('Financial settings not available:', err);
      }

      // Load complaint allowances
      try {
        const complaintResponse = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/complaint-allowances`);
        if (complaintResponse.ok) {
          const complaintData = await complaintResponse.json();
          if (complaintData.success) {
            setComplaintAllowances(complaintData.allowances || []);
          }
        }
      } catch (err) {
        console.warn('Complaint allowances not available:', err);
      }

      // Load group discounts
      try {
        const groupDiscountsRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/group-discounts`);
        if (groupDiscountsRes.ok) {
          const groupDiscountsData = await groupDiscountsRes.json();
          if (groupDiscountsData.success) {
            setGroupDiscounts(groupDiscountsData.discounts || []);
          }
        }
      } catch (err) {
        console.warn('Group discounts not available:', err);
      }

      // Load package deals
      try {
        const packagesRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/package-deals`);
        if (packagesRes.ok) {
          const packagesData = await packagesRes.json();
          if (packagesData.success) {
            setPackageDeals(packagesData.packages || []);
          }
        }
      } catch (err) {
        console.warn('Package deals not available:', err);
      }

      // Load cancellation policy
      try {
        const policyRes = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/cancellation-policy`);
        if (policyRes.ok) {
          const policyData = await policyRes.json();
          if (policyData.success) {
            setCancellationPolicy(policyData.policy || []);
            setPolicyIsCustom(policyData.is_custom || false);
          }
        }
      } catch (err) {
        console.error('Error loading cancellation policy:', err);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setHotelSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveHotelSettings = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(hotelSettings)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Settings saved successfully!');
      } else {
        throw new Error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Taxes Management
  const addTax = () => {
    setTaxes([...taxes, {
      id: `tax_${Date.now()}`,
      name: '',
      rate: 0,
      type: 'percentage',
      enabled: true
    }]);
  };

  const updateTax = (id, field, value) => {
    setTaxes(taxes.map(tax => 
      tax.id === id ? { ...tax, [field]: value } : tax
    ));
  };

  const removeTax = (id) => {
    setTaxes(taxes.filter(tax => tax.id !== id));
  };

  const handleSaveTaxes = async () => {
    const invalidTaxes = taxes.filter(t => !t.name || t.rate <= 0);
    if (invalidTaxes.length > 0) {
      toast.error('Please fill in all tax names and rates');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/financial-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxes, fees })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Taxes saved successfully');
      } else {
        toast.error(data.error || 'Failed to save taxes');
      }
    } catch (error) {
      console.error('Failed to save taxes:', error);
      toast.error('Failed to save taxes');
    } finally {
      setSaving(false);
    }
  };

  // Fees Management
  const addFee = () => {
    setFees([...fees, {
      id: `fee_${Date.now()}`,
      name: '',
      amount: 0,
      type: 'fixed',
      enabled: true
    }]);
  };

  const updateFee = (id, field, value) => {
    setFees(fees.map(fee => 
      fee.id === id ? { ...fee, [field]: value } : fee
    ));
  };

  const removeFee = (id) => {
    setFees(fees.filter(fee => fee.id !== id));
  };

  const handleSaveFees = async () => {
    const invalidFees = fees.filter(f => !f.name || f.amount <= 0);
    if (invalidFees.length > 0) {
      toast.error('Please fill in all fee names and amounts');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/financial-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taxes, fees })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Fees saved successfully');
      } else {
        toast.error(data.error || 'Failed to save fees');
      }
    } catch (error) {
      console.error('Failed to save fees:', error);
      toast.error('Failed to save fees');
    } finally {
      setSaving(false);
    }
  };

  // Complaint Allowances Management
  const addComplaintAllowance = () => {
    setComplaintAllowances([...complaintAllowances, {
      id: `allowance_${Date.now()}`,
      complaint_type: '',
      max_amount: 0,
      action_type: 'discount', // discount, refund, upgrade, complimentary
      requires_manager: false,
      notify_manager: true,
      frontdesk_can_approve: true,
      enabled: true
    }]);
  };

  const updateComplaintAllowance = (id, field, value) => {
    setComplaintAllowances(complaintAllowances.map(allowance => 
      allowance.id === id ? { ...allowance, [field]: value } : allowance
    ));
  };

  const removeComplaintAllowance = (id) => {
    setComplaintAllowances(complaintAllowances.filter(allowance => allowance.id !== id));
  };

  const handleSaveComplaintAllowances = async () => {
    const invalidAllowances = complaintAllowances.filter(a => !a.complaint_type || a.max_amount <= 0);
    if (invalidAllowances.length > 0) {
      toast.error('Please fill in all complaint types and amounts');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/complaint-allowances`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowances: complaintAllowances })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Complaint allowances saved successfully');
      } else {
        toast.error(data.error || 'Failed to save complaint allowances');
      }
    } catch (error) {
      console.error('Failed to save complaint allowances:', error);
      toast.error('Failed to save complaint allowances');
    } finally {
      setSaving(false);
    }
  };

  // Group Discounts Management
  const addGroupDiscount = () => {
    setGroupDiscounts([...groupDiscounts, {
      id: `discount_${Date.now()}`,
      name: '',
      min_rooms: 1,
      min_nights: 1,
      discount_percent: 0,
      active: true
    }]);
  };

  const updateGroupDiscount = (id, field, value) => {
    setGroupDiscounts(groupDiscounts.map(discount => 
      discount.id === id ? { ...discount, [field]: value } : discount
    ));
  };

  const removeGroupDiscount = (id) => {
    setGroupDiscounts(groupDiscounts.filter(discount => discount.id !== id));
  };

  const handleSaveGroupDiscounts = async () => {
    const invalid = groupDiscounts.filter(d => !d.name || d.min_rooms <= 0 || d.min_nights <= 0 || d.discount_percent <= 0);
    if (invalid.length > 0) {
      toast.error('Please fill in all fields correctly');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/group-discounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discounts: groupDiscounts })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Group discounts saved successfully');
      } else {
        toast.error(data.error || 'Failed to save group discounts');
      }
    } catch (error) {
      console.error('Failed to save group discounts:', error);
      toast.error('Failed to save group discounts');
    } finally {
      setSaving(false);
    }
  };

  // Package Deals Management
  const addPackageDeal = () => {
    setPackageDeals([...packageDeals, {
      id: `package_${Date.now()}`,
      name: '',
      meeting_room_id: '',
      guest_rooms_count: 1,
      duration_type: 'full_day',
      package_price: 0,
      active: true
    }]);
  };

  const updatePackageDeal = (id, field, value) => {
    setPackageDeals(packageDeals.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const removePackageDeal = (id) => {
    setPackageDeals(packageDeals.filter(pkg => pkg.id !== id));
  };

  const handleSavePackageDeals = async () => {
    const invalid = packageDeals.filter(p => !p.name || !p.meeting_room_id || p.guest_rooms_count <= 0 || p.package_price <= 0);
    if (invalid.length > 0) {
      toast.error('Please fill in all package fields correctly');
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/package-deals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packages: packageDeals })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Package deals saved successfully');
      } else {
        toast.error(data.error || 'Failed to save package deals');
      }
    } catch (error) {
      console.error('Failed to save package deals:', error);
      toast.error('Failed to save package deals');
    } finally {
      setSaving(false);
    }
  };

  // Stripe Connect Functions
  const loadStripeStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stripe/connect/status/${hotelId}`);
      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
      }
    } catch (error) {
      console.error('Error loading Stripe status:', error);
    }
  };

  const handleStripeConnect = async () => {
    try {
      setStripeLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/stripe/connect/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          refresh_url: `${window.location.origin}/dashboard/settings?refresh=true`,
          return_url: `${window.location.origin}/dashboard/settings?connected=true`
        })
      });

      const data = await response.json();

      if (data.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = data.onboarding_url;
      } else {
        throw new Error('No onboarding URL received');
      }
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      toast.error('Failed to start Stripe Connect onboarding');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefreshStripeStatus = async () => {
    try {
      setStripeLoading(true);
      await loadStripeStatus();
      toast.success('Stripe status refreshed');
    } catch (error) {
      console.error('Error refreshing Stripe status:', error);
      toast.error('Failed to refresh Stripe status');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? This will disable payment processing.')) {
      return;
    }

    try {
      setStripeLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/stripe/connect/disconnect/${hotelId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Stripe account disconnected');
        await loadStripeStatus();
      } else {
        throw new Error(data.detail || 'Failed to disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting Stripe:', error);
      toast.error('Failed to disconnect Stripe account');
    } finally {
      setStripeLoading(false);
    }
  };

  const copyAdminLink = async () => {
    const adminLink = `${PREVIEW_URL}/admin/${hotel?.slug || hotel?.id}`;
    try {
      await navigator.clipboard.writeText(adminLink);
      toast.success('Admin link copied!');
    } catch (err) {
      toast.info('Link: ' + adminLink, {
        duration: 10000,
        description: 'Copy this link manually'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="unified-settings-content">
      <div>
        <h2 className="text-2xl font-bold heading-font mb-2">Settings</h2>
        <p className="text-muted-foreground">Manage all hotel settings in one place</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="w-full p-1 bg-muted/50 flex items-center justify-between gap-1">
          <TabsTrigger 
            value="info" 
            className="flex-1 flex items-center justify-center gap-2 text-blue-600 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Info</span>
          </TabsTrigger>
          <TabsTrigger 
            value="branding" 
            className="flex-1 flex items-center justify-center gap-2 text-purple-600 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Palette className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Branding</span>
          </TabsTrigger>
          <TabsTrigger 
            value="financial" 
            className="flex-1 flex items-center justify-center gap-2 text-emerald-600 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <DollarSign className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Financial</span>
          </TabsTrigger>
          <TabsTrigger 
            value="payments" 
            className="flex-1 flex items-center justify-center gap-2 text-cyan-600 data-[state=active]:bg-cyan-100 data-[state=active]:text-cyan-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <CreditCard className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Payments</span>
          </TabsTrigger>
          <TabsTrigger 
            value="taxes" 
            className="flex-1 flex items-center justify-center gap-2 text-orange-600 data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Percent className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Taxes</span>
          </TabsTrigger>
          <TabsTrigger 
            value="fees" 
            className="flex-1 flex items-center justify-center gap-2 text-amber-600 data-[state=active]:bg-amber-100 data-[state=active]:text-amber-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Receipt className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Fees</span>
          </TabsTrigger>
          <TabsTrigger 
            value="terms" 
            className="flex-1 flex items-center justify-center gap-2 text-slate-600 data-[state=active]:bg-slate-100 data-[state=active]:text-slate-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Terms</span>
          </TabsTrigger>
          <TabsTrigger 
            value="verification" 
            className="flex-1 flex items-center justify-center gap-2 text-teal-600 data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <CheckCircle className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Verify</span>
          </TabsTrigger>
          <TabsTrigger 
            value="reliability" 
            className="flex-1 flex items-center justify-center gap-2 text-yellow-600 data-[state=active]:bg-yellow-100 data-[state=active]:text-yellow-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Star className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Reliability</span>
          </TabsTrigger>
          <TabsTrigger 
            value="complaints" 
            className="flex-1 flex items-center justify-center gap-2 text-rose-600 data-[state=active]:bg-rose-100 data-[state=active]:text-rose-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Shield className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Complaints</span>
          </TabsTrigger>
          <TabsTrigger 
            value="cancellation" 
            className="flex-1 flex items-center justify-center gap-2 text-indigo-600 data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
          >
            <Calendar className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">Cancellation</span>
          </TabsTrigger>
          <TabsTrigger 
            value="voice" 
            className="flex-1 flex items-center justify-center gap-2 text-purple-600 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 data-[state=active]:shadow-sm data-[state=active]:font-semibold"
            data-testid="settings-voice-tab"
          >
            <Volume2 className="w-4 h-4" />
            <span className="hidden lg:inline font-medium text-xs xl:text-sm">AI Voice</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Hotel Info */}
        <TabsContent value="info" className="space-y-6">
          {/* Admin Portal Link */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base text-blue-900">
                <Globe className="w-5 h-5 text-blue-600" />
                Your Personalized Admin Portal Link
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-blue-800">
                This is your unique dashboard link with your hotel name. Bookmark it for instant access!
              </p>
              <div className="bg-white rounded-lg p-3 flex items-center justify-between gap-3 shadow-sm">
                <code className="mono text-sm flex-1 break-all text-blue-900">
                  {PREVIEW_URL}/admin/{hotel?.slug || hotel?.id}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyAdminLink}
                  data-testid="copy-admin-link-btn"
                  className="border-blue-300 hover:bg-blue-50"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                  <Building2 className="w-3 h-3 mr-1" />
                  {hotel?.name}
                </Badge>
                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Auto-login enabled
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card className="border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Building2 className="w-5 h-5 text-blue-600" />
                Basic Information
              </CardTitle>
              <CardDescription>
                This information will appear on registration cards, folios, and guest communications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="hotel_name" className="text-base font-semibold">Hotel Name</Label>
                <Input
                  id="hotel_name"
                  value={hotelSettings.hotel_name}
                  onChange={(e) => handleSettingsChange('hotel_name', e.target.value)}
                  placeholder="The Grand Marina Hotel"
                  className="text-base"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hotel_address" className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Full Address
                </Label>
                <Textarea
                  id="hotel_address"
                  value={hotelSettings.hotel_address}
                  onChange={(e) => handleSettingsChange('hotel_address', e.target.value)}
                  placeholder="123 Marina Boulevard, Dubai, UAE"
                  rows={2}
                  className="text-base"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="hotel_email" className="text-base font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Contact Email
                  </Label>
                  <Input
                    id="hotel_email"
                    type="email"
                    value={hotelSettings.hotel_email}
                    onChange={(e) => handleSettingsChange('hotel_email', e.target.value)}
                    placeholder="contact@grandmarina.com"
                    className="text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hotel_phone" className="text-base font-semibold flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Phone
                  </Label>
                  <Input
                    id="hotel_phone"
                    value={hotelSettings.hotel_phone}
                    onChange={(e) => handleSettingsChange('hotel_phone', e.target.value)}
                    placeholder="+971 4 XXX XXXX"
                    className="text-base"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hotel_website" className="text-base font-semibold flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Website (Optional)
                </Label>
                <Input
                  id="hotel_website"
                  value={hotelSettings.hotel_website}
                  onChange={(e) => handleSettingsChange('hotel_website', e.target.value)}
                  placeholder="https://www.grandmarina.com"
                  className="text-base"
                />
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="hotel_logo_url" className="text-base font-semibold flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Hotel Logo URL
                </Label>
                <Input
                  id="hotel_logo_url"
                  value={hotelSettings.hotel_logo_url}
                  onChange={(e) => handleSettingsChange('hotel_logo_url', e.target.value)}
                  placeholder="https://example.com/logo.png"
                  className="text-base"
                />
                <p className="text-sm text-muted-foreground">
                  Enter a URL to your hotel logo. Recommended size: 200x200px
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveHotelSettings}
                  disabled={saving}
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Hotel Info'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Branding */}
        <TabsContent value="branding" className="space-y-6">
          <Card className="border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Palette className="w-5 h-5 text-purple-600" />
                Brand Colors
              </CardTitle>
              <CardDescription>
                These colors will be used in print templates (registration cards, folios)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="primary_color" className="text-base font-semibold">Primary Color</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="primary_color"
                      type="color"
                      value={hotelSettings.primary_color}
                      onChange={(e) => handleSettingsChange('primary_color', e.target.value)}
                      className="w-20 h-12 cursor-pointer"
                    />
                    <Input
                      value={hotelSettings.primary_color}
                      onChange={(e) => handleSettingsChange('primary_color', e.target.value)}
                      placeholder="#10b981"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Used for headers and primary elements</p>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="secondary_color" className="text-base font-semibold">Secondary Color</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="secondary_color"
                      type="color"
                      value={hotelSettings.secondary_color}
                      onChange={(e) => handleSettingsChange('secondary_color', e.target.value)}
                      className="w-20 h-12 cursor-pointer"
                    />
                    <Input
                      value={hotelSettings.secondary_color}
                      onChange={(e) => handleSettingsChange('secondary_color', e.target.value)}
                      placeholder="#3b82f6"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Used for accents and highlights</p>
                </div>
              </div>

              <div className="mt-6 p-6 bg-muted rounded-xl border">
                <p className="font-semibold mb-4">Color Preview:</p>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    className="h-24 rounded-lg shadow-md flex items-center justify-center text-white font-bold"
                    style={{ background: `linear-gradient(135deg, ${hotelSettings.primary_color} 0%, ${hotelSettings.secondary_color} 100%)` }}
                  >
                    Header Gradient
                  </div>
                  <div className="space-y-2">
                    <div 
                      className="h-10 rounded-lg"
                      style={{ backgroundColor: hotelSettings.primary_color }}
                    ></div>
                    <div 
                      className="h-10 rounded-lg"
                      style={{ backgroundColor: hotelSettings.secondary_color }}
                    ></div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveHotelSettings}
                  disabled={saving}
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Branding'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3: Financial */}
        <TabsContent value="financial" className="space-y-6">
          {/* Wallet Addresses (Editable) */}
          <WalletAddresses hotelId={hotelId} readOnly={false} />

          {/* Payment Integrations */}
          <Card className="border-emerald-200">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-emerald-900">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Payment Integrations
              </CardTitle>
              <CardDescription>
                Configure payment methods (Coming Soon: Stripe, Google Pay, Apple Pay)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Payment integrations will be available soon. Currently accepting crypto payments only.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Currency Settings */}
          <Card className="border-emerald-200">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-transparent">
              <CardTitle className="text-emerald-900">Currency Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency Code</Label>
                  <Input
                    id="currency"
                    value={hotelSettings.currency}
                    onChange={(e) => handleSettingsChange('currency', e.target.value.toUpperCase())}
                    placeholder="USD"
                    maxLength={3}
                  />
                  <p className="text-sm text-muted-foreground">3-letter code (USD, EUR, AED, etc.)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency_symbol">Currency Symbol</Label>
                  <Input
                    id="currency_symbol"
                    value={hotelSettings.currency_symbol}
                    onChange={(e) => handleSettingsChange('currency_symbol', e.target.value)}
                    placeholder="$"
                    maxLength={3}
                  />
                  <p className="text-sm text-muted-foreground">Symbol (e.g., $, €, د.إ)</p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveHotelSettings}
                  disabled={saving}
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Currency Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 3.5: Payments (Stripe Connect) */}
        <TabsContent value="payments" className="space-y-6">
          <Card data-testid="payments-settings-card" className="border-cyan-200">
            <CardHeader className="bg-gradient-to-r from-cyan-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <CardTitle className="text-lg heading-font text-cyan-900">Payment Processing</CardTitle>
                  <CardDescription className="text-cyan-700">
                    Connect your Stripe account to receive payments from guests
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Test Mode Notice */}
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <span className="font-semibold">Test Mode Active:</span> Currently using platform test keys for development. 
                  Connect your real Stripe account to process live payments. No credit card data is stored (PCI compliant).
                </AlertDescription>
              </Alert>

              {/* Connection Status */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold heading-font flex items-center gap-2">
                      Connection Status
                      {stripeStatus.connected && (
                        <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                          Connected
                        </Badge>
                      )}
                      {!stripeStatus.connected && (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-800">
                          Not Connected
                        </Badge>
                      )}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {stripeStatus.connected 
                        ? 'Your Stripe account is linked and ready to process payments'
                        : 'Connect your Stripe account to start accepting payments'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshStripeStatus}
                    disabled={stripeLoading}
                    data-testid="refresh-stripe-status-btn"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${stripeLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {/* Account Details */}
                {stripeStatus.connected && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Account ID</p>
                      <p className="font-mono text-sm">{stripeStatus.account_id}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Charges Enabled</p>
                      <div className="flex items-center gap-2">
                        {stripeStatus.charges_enabled ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payouts Enabled</p>
                      <div className="flex items-center gap-2">
                        {stripeStatus.payouts_enabled ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            No
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Details Submitted</p>
                      <div className="flex items-center gap-2">
                        {stripeStatus.details_submitted ? (
                          <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Complete
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Incomplete
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Requirements (if any) */}
                {stripeStatus.connected && stripeStatus.requirements && 
                 (stripeStatus.requirements.currently_due?.length > 0 || stripeStatus.requirements.past_due?.length > 0) && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <p className="font-semibold mb-2">Action Required:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {stripeStatus.requirements.currently_due?.map((req, idx) => (
                          <li key={idx}>{req.replace(/_/g, ' ')}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {!stripeStatus.connected ? (
                  <Button
                    onClick={handleStripeConnect}
                    disabled={stripeLoading}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    data-testid="connect-stripe-btn"
                  >
                    {stripeLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Connect Stripe Account
                  </Button>
                ) : (
                  <>
                    {!stripeStatus.details_submitted && (
                      <Button
                        onClick={handleStripeConnect}
                        disabled={stripeLoading}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                        data-testid="complete-stripe-onboarding-btn"
                      >
                        {stripeLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Complete Onboarding
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={handleDisconnectStripe}
                      disabled={stripeLoading}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="disconnect-stripe-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Disconnect (Test Only)
                    </Button>
                  </>
                )}
              </div>

              {/* Platform Fee Info */}
              <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-sm heading-font mb-2 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-slate-600" />
                  Platform Fee Structure
                </h4>
                <p className="text-sm text-muted-foreground">
                  getbitsy.ai charges a <span className="font-bold text-slate-900">6% platform fee</span> on all successful bookings. 
                  This fee covers payment processing, NFT minting (for crypto bookings), marketplace hosting, and platform maintenance.
                </p>
                <div className="mt-3 p-3 bg-white rounded border border-slate-200">
                  <p className="text-xs text-muted-foreground mb-2">Example Breakdown:</p>
                  <div className="flex justify-between text-sm">
                    <span>Guest pays:</span>
                    <span className="font-semibold">$100.00</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Platform fee (6%):</span>
                    <span>-$6.00</span>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-sm font-bold">
                    <span>You receive:</span>
                    <span className="text-green-700">$94.00</span>
                  </div>
                </div>
              </div>

              {/* PCI Compliance Notice */}
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-green-900 mb-1">PCI Compliant & Secure</p>
                  <p className="text-green-800">
                    All payment data is handled directly by Stripe. No credit card numbers, CVVs, or sensitive data 
                    is ever stored on getbitsy.ai servers. Stripe maintains Level 1 PCI DSS compliance.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 4: Taxes */}
        <TabsContent value="taxes" className="space-y-6">
          <Card data-testid="taxes-settings-card" className="border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="heading-font text-orange-900">Taxes</CardTitle>
                  <CardDescription className="mt-1">
                    Add multiple tax types (City Tax, State Tax, VAT, Tourism Tax, etc.)
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Configure taxes that will be applied to bookings
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addTax}
                  data-testid="add-tax-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tax
                </Button>
              </div>

              {taxes.length === 0 ? (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    No taxes configured. Click "Add Tax" to create your first tax line.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {taxes.map((tax, index) => (
                    <div
                      key={tax.id}
                      className="grid md:grid-cols-[1fr_150px_100px_auto] gap-3 items-end p-4 rounded-lg border bg-card"
                      data-testid={`tax-item-${index}`}
                    >
                      <div className="space-y-2">
                        <Label htmlFor={`tax-name-${tax.id}`}>
                          Tax Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id={`tax-name-${tax.id}`}
                          value={tax.name}
                          onChange={(e) => updateTax(tax.id, 'name', e.target.value)}
                          placeholder="e.g., City Tax, VAT, Tourism Tax"
                          data-testid={`tax-name-${index}`}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`tax-rate-${tax.id}`}>
                          Rate <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id={`tax-rate-${tax.id}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={tax.rate}
                            onChange={(e) => updateTax(tax.id, 'rate', parseFloat(e.target.value))}
                            placeholder="10"
                            data-testid={`tax-rate-${index}`}
                          />
                          <Percent className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`tax-type-${tax.id}`}>Type</Label>
                        <Select
                          value={tax.type}
                          onValueChange={(value) => updateTax(tax.id, 'type', value)}
                        >
                          <SelectTrigger id={`tax-type-${tax.id}`} data-testid={`tax-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">%</SelectItem>
                            <SelectItem value="fixed">$</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeTax(tax.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`remove-tax-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveTaxes}
                  disabled={saving || taxes.length === 0}
                  size="lg"
                  data-testid="save-taxes-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Taxes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 5: Fees */}
        <TabsContent value="fees" className="space-y-6">
          <Card data-testid="fees-settings-card" className="border-amber-200">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="heading-font text-amber-900">Additional Fees</CardTitle>
                  <CardDescription className="mt-1">
                    Add optional fees (Pet Fee, Smoking Fee, Damage Deposit, Cleaning Fee, etc.)
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Configure fees that can be applied to bookings
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addFee}
                  data-testid="add-fee-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Fee
                </Button>
              </div>

              {fees.length === 0 ? (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    No fees configured. Click "Add Fee" to create your first fee line.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {fees.map((fee, index) => (
                    <div
                      key={fee.id}
                      className="grid md:grid-cols-[1fr_150px_120px_auto] gap-3 items-end p-4 rounded-lg border bg-card"
                      data-testid={`fee-item-${index}`}
                    >
                      <div className="space-y-2">
                        <Label htmlFor={`fee-name-${fee.id}`}>
                          Fee Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id={`fee-name-${fee.id}`}
                          value={fee.name}
                          onChange={(e) => updateFee(fee.id, 'name', e.target.value)}
                          placeholder="e.g., Pet Fee, Smoking Fee"
                          data-testid={`fee-name-${index}`}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`fee-amount-${fee.id}`}>
                          Amount <span className="text-destructive">*</span>
                        </Label>
                        <div className="relative">
                          <Input
                            id={`fee-amount-${fee.id}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={fee.amount}
                            onChange={(e) => updateFee(fee.id, 'amount', parseFloat(e.target.value))}
                            placeholder="50"
                            data-testid={`fee-amount-${index}`}
                          />
                          <DollarSign className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`fee-type-${fee.id}`}>Type</Label>
                        <Select
                          value={fee.type}
                          onValueChange={(value) => updateFee(fee.id, 'type', value)}
                        >
                          <SelectTrigger id={`fee-type-${fee.id}`} data-testid={`fee-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed</SelectItem>
                            <SelectItem value="per_night">Per Night</SelectItem>
                            <SelectItem value="percentage">% of Total</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFee(fee.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`remove-fee-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveFees}
                  disabled={saving || fees.length === 0}
                  size="lg"
                  data-testid="save-fees-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Fees'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 6: Terms */}
        <TabsContent value="terms" className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-transparent">
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <FileText className="w-5 h-5 text-slate-600" />
                Terms & Conditions
              </CardTitle>
              <CardDescription>
                Customize the legal text that appears on registration cards and folios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="registration_terms" className="text-base font-semibold">
                  Registration Card Terms
                </Label>
                <Textarea
                  id="registration_terms"
                  value={hotelSettings.registration_terms}
                  onChange={(e) => handleSettingsChange('registration_terms', e.target.value)}
                  rows={5}
                  className="text-sm"
                  placeholder="By signing below, I acknowledge..."
                />
                <p className="text-sm text-muted-foreground">
                  This text appears on the registration card that guests sign at check-in
                </p>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label htmlFor="folio_terms" className="text-base font-semibold">
                  Guest Folio Footer Text
                </Label>
                <Textarea
                  id="folio_terms"
                  value={hotelSettings.folio_terms}
                  onChange={(e) => handleSettingsChange('folio_terms', e.target.value)}
                  rows={3}
                  className="text-sm"
                  placeholder="Thank you for staying with us..."
                />
                <p className="text-sm text-muted-foreground">
                  This text appears at the bottom of guest folios
                </p>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveHotelSettings}
                  disabled={saving}
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Terms'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 7: Verification */}
        <TabsContent value="verification" className="space-y-6">
          <HotelVerificationPanel hotelId={hotelId} ownerEmail={user?.email} />
        </TabsContent>

        {/* Tab 8: Reliability */}
        <TabsContent value="reliability" className="space-y-6">
          <ReliabilitySettingsPanel hotelId={hotelId} />
        </TabsContent>

        {/* Tab 9: Complaint Allowances */}
        <TabsContent value="complaints" className="space-y-6">
          <Card data-testid="complaint-allowances-card" className="border-rose-200">
            <CardHeader className="bg-gradient-to-r from-rose-50 to-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <CardTitle className="heading-font text-rose-900">Complaint Allowances</CardTitle>
                  <CardDescription className="mt-1">
                    Empower front desk staff to resolve guest complaints quickly with pre-approved allowances
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <Alert className="bg-rose-50 border-rose-200">
                <AlertCircle className="w-4 h-4 text-rose-600" />
                <AlertDescription className="text-sm text-rose-900">
                  Set maximum discount/refund amounts that front desk staff can approve without manager intervention. This speeds up complaint resolution and improves guest satisfaction.
                </AlertDescription>
              </Alert>

              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Configure complaint types and their resolution allowances
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addComplaintAllowance}
                  data-testid="add-complaint-allowance-button"
                  className="border-rose-300 hover:bg-rose-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Complaint Type
                </Button>
              </div>

              {complaintAllowances.length === 0 ? (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    No complaint allowances configured. Click "Add Complaint Type" to empower your front desk team.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {complaintAllowances.map((allowance, index) => (
                    <div
                      key={allowance.id}
                      className="p-5 rounded-lg border bg-card space-y-4"
                      data-testid={`complaint-allowance-${index}`}
                    >
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`complaint-type-${allowance.id}`}>
                            Complaint Type <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id={`complaint-type-${allowance.id}`}
                            value={allowance.complaint_type}
                            onChange={(e) => updateComplaintAllowance(allowance.id, 'complaint_type', e.target.value)}
                            placeholder="e.g., Room Cleanliness, AC Not Working, Noise Issue"
                            data-testid={`complaint-type-${index}`}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`action-type-${allowance.id}`}>
                            Action Type
                          </Label>
                          <Select
                            value={allowance.action_type}
                            onValueChange={(value) => updateComplaintAllowance(allowance.id, 'action_type', value)}
                          >
                            <SelectTrigger id={`action-type-${allowance.id}`} data-testid={`action-type-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="discount">Discount</SelectItem>
                              <SelectItem value="refund">Refund</SelectItem>
                              <SelectItem value="upgrade">Room Upgrade</SelectItem>
                              <SelectItem value="complimentary">Complimentary Service</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`max-amount-${allowance.id}`}>
                            Maximum Amount ($) <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id={`max-amount-${allowance.id}`}
                            type="number"
                            min="0"
                            step="1"
                            value={allowance.max_amount}
                            onChange={(e) => updateComplaintAllowance(allowance.id, 'max_amount', parseFloat(e.target.value))}
                            placeholder="50"
                            data-testid={`max-amount-${index}`}
                          />
                          <p className="text-xs text-muted-foreground">
                            Front desk can approve up to this amount
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allowance.frontdesk_can_approve}
                              onChange={(e) => updateComplaintAllowance(allowance.id, 'frontdesk_can_approve', e.target.checked)}
                              className="rounded"
                            />
                            Front Desk Can Approve
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Allow front desk to resolve without waiting
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allowance.notify_manager}
                              onChange={(e) => updateComplaintAllowance(allowance.id, 'notify_manager', e.target.checked)}
                              className="rounded"
                            />
                            Notify Manager
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Send notification after resolution
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeComplaintAllowance(allowance.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`remove-complaint-${index}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <Button
                  onClick={handleSaveComplaintAllowances}
                  disabled={saving || complaintAllowances.length === 0}
                  size="lg"
                  data-testid="save-complaint-allowances-button"
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Complaint Allowances'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* Tab 10: Cancellation Policy */}
        <TabsContent value="cancellation" className="space-y-6">
          <Card data-testid="cancellation-policy-card" className="border-indigo-200">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="heading-font text-indigo-900">Cancellation Policy</CardTitle>
                    <CardDescription className="mt-1">
                      Define your own pricing tiers and refund percentages
                    </CardDescription>
                  </div>
                </div>
                {policyIsCustom && (
                  <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-300">
                    Custom Policy
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {cancellationPolicy.length === 0 ? (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    Loading cancellation policy...
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="space-y-4">
                    {cancellationPolicy.map((tier, index) => (
                      <div
                        key={index}
                        className="p-5 rounded-xl border-2 bg-gradient-to-br from-white to-indigo-50/30 hover:border-indigo-300 transition-colors"
                        data-testid={`policy-tier-${index}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-bold text-lg text-indigo-900">{tier.name}</h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {tier.days_max === null 
                                ? `${tier.days_min}+ days before check-in`
                                : `${tier.days_min}-${tier.days_max} days before check-in`
                              }
                            </p>
                          </div>
                          <Badge 
                            variant={tier.price_adjustment_pct < 0 ? "default" : tier.price_adjustment_pct > 0 ? "destructive" : "secondary"}
                            className="text-sm px-3 py-1"
                          >
                            {tier.price_adjustment_pct > 0 ? '+' : ''}{tier.price_adjustment_pct}% {tier.price_adjustment_pct < 0 ? 'discount' : tier.price_adjustment_pct > 0 ? 'surcharge' : 'regular'}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-4 p-4 bg-white/70 rounded-lg border">
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Base Refund</p>
                            <p className="text-2xl font-bold text-indigo-600">{tier.base_refund_pct}%</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Price Adjustment</p>
                            <p className="text-2xl font-bold text-slate-700">
                              {tier.price_adjustment_pct > 0 ? '+' : ''}{tier.price_adjustment_pct}%
                            </p>
                          </div>
                        </div>

                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-800">
                            <strong>Example:</strong> Guest books $1000 → Pays ${(1000 * (1 + tier.price_adjustment_pct / 100)).toFixed(0)} | If cancels → Gets ${(1000 * (1 + tier.price_adjustment_pct / 100) * (tier.base_refund_pct / 100)).toFixed(0)} back ({tier.base_refund_pct}%)
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertCircle className="w-4 h-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>How it works:</strong> Guests book based on days in advance. The refund percentage is <strong>locked at booking time</strong>. 
                      If they cancel later, they get the locked percentage (plus reliability bonus if applicable).
                    </AlertDescription>
                  </Alert>

                  <div className="flex justify-between items-center pt-4">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/hotels/${hotelId}/cancellation-policy`);
                          const data = await response.json();
                          if (data.success) {
                            setCancellationPolicy(data.policy);
                            setPolicyIsCustom(data.is_custom);
                            toast.success('Policy refreshed');
                          }
                        } catch (error) {
                          toast.error('Failed to refresh policy');
                        }
                      }}
                      data-testid="refresh-policy-button"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                    
                    <div className="text-sm text-muted-foreground">
                      {policyIsCustom ? (
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                          Using your custom policy
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                          Using system default policy
                        </span>
                      )}
                    </div>
                  </div>

                  <Alert>
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>
                      <strong>Want to customize?</strong> Contact support or use the API to create your own policy tiers. 
                      You can define unlimited tiers with custom day ranges, discounts, and refund percentages.
                    </AlertDescription>
                  </Alert>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Voice Config Tab */}
        <TabsContent value="voice" className="space-y-6" data-testid="voice-settings-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Volume2 className="w-5 h-5 text-purple-600" />
                AI Agent Voice Configuration
              </CardTitle>
              <CardDescription>
                Customize how each AI agent sounds. Different voices help guests distinguish between agents and reinforce your brand identity.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-400" /> Bitsy (Concierge)
                  </h3>
                  <VoiceConfigPanel hotelId={hotelId} agent="bitsy" compact />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-400" /> Marina (Staff)
                  </h3>
                  <VoiceConfigPanel hotelId={hotelId} agent="marina" compact />
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400" /> Sage (Manager)
                  </h3>
                  <VoiceConfigPanel hotelId={hotelId} agent="sage" compact />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
